import React, { useState, useEffect, useCallback, useRef } from "react";
import { marked } from "marked";

marked.setOptions({ breaks: true });
if (marked.Renderer) {
	const renderer = new marked.Renderer();
	renderer.link = function (href, title, text) {
		const h = (href || "").trim();
		const t = (title || "").trim();
		return (
			'<a href="' +
			h +
			'" target="_blank" rel="noopener noreferrer"' +
			(t ? ' title="' + t + '"' : "") +
			">" +
			(text || h) +
			"</a>"
		);
	};
	marked.use({ renderer });
}

function stripDisplayLanguagePrefix(text) {
	if (!text || typeof text !== "string") return "";
	const s = String(text);
	const re = /^\s*(English|Spanish)\s*[:\-.]?\s*[\r\n]+/i;
	if (re.test(s)) return s.replace(re, "").trimStart();
	return s;
}

function stripCitationIds(text) {
	if (!text) return "";
	let s = String(text)
		.replace(/\[\s*ID\s*:\s*\d+\s*\]/gi, "")
		.replace(/\s*\[\d*\]\s*/g, "\n")
		.replace(/\s*\[\s*\]\s*/g, "\n")
		.replace(/\s*\(\s*marked\s+not\s+used\s*\)\s*/gi, "\n")
		.replace(/\s*marked\s+not\s+used\s*/gi, "\n");
	return s.replace(/[ \t]+/g, " ").trim();
}

export function parseAssistantMarkdown(text) {
	if (!text || typeof text !== "string") return "";
	const stripped = stripCitationIds(stripDisplayLanguagePrefix(text));
	try {
		let html = marked.parse(stripped);
		if (typeof html === "string") {
			html = html.replace(/<a (?![^>]*target=)/gi, '<a target="_blank" rel="noopener noreferrer" ');
		}
		return html;
	} catch (e) {
		return text;
	}
}

function resolveUrlAgainstWebsite(raw, websiteBaseUrl) {
	const s = String(raw ?? "").trim();
	if (!s) return "";
	if (/^(https?:|\/\/|mailto:|tel:|data:)/i.test(s)) return s;
	const base = String(websiteBaseUrl ?? "").trim().replace(/\/+$/, "");
	if (!base) return s;
	return s.startsWith("/") ? `${base}${s}` : `${base}/${s}`;
}

const SLIDER_CARD_W = 120;
const SLIDER_GAP = 10;

export function ProductSlider({ products, websiteBaseUrl }) {
	const trackRef = useRef(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);

	const updateArrows = useCallback(() => {
		const el = trackRef.current;
		if (!el) return;
		const { scrollLeft, scrollWidth, clientWidth } = el;
		const maxScroll = scrollWidth - clientWidth;
		setCanPrev(scrollLeft > 2);
		setCanNext(maxScroll > 2 && scrollLeft < maxScroll - 2);
	}, []);

	useEffect(() => {
		const el = trackRef.current;
		if (!el) return;
		updateArrows();
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
		if (ro) ro.observe(el);
		el.addEventListener("scroll", updateArrows, { passive: true });
		return () => {
			if (ro) ro.disconnect();
			el.removeEventListener("scroll", updateArrows);
		};
	}, [products, updateArrows]);

	const scrollByDir = (dir) => {
		const el = trackRef.current;
		if (!el) return;
		const step = SLIDER_CARD_W + SLIDER_GAP;
		el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
	};

	if (!products || !products.length) return null;

	return (
		<div
			className="mt-2 pt-2 sa-conv-product-slider-wrap"
			style={{
				borderTop: "1px solid rgba(0,0,0,0.08)",
				marginLeft: -4,
				marginRight: -4,
			}}
		>
			<div className="small text-muted mb-1" style={{ fontSize: 11, fontWeight: 600 }}>
				Products
			</div>
			<style>{`
				.sa-conv-product-slider-track {
					overflow-x: auto;
					scroll-behavior: smooth;
					-webkit-overflow-scrolling: touch;
					scrollbar-width: none;
					-ms-overflow-style: none;
				}
				.sa-conv-product-slider-track::-webkit-scrollbar {
					display: none;
					width: 0;
					height: 0;
				}
				.sa-conv-product-slider-btn {
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					z-index: 2;
					width: 32px;
					height: 32px;
					border-radius: 50%;
					border: 1px solid #dee2e6;
					background: #fff;
					box-shadow: 0 1px 3px rgba(0,0,0,0.12);
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					color: #495057;
					padding: 0;
					line-height: 1;
				}
				.sa-conv-product-slider-btn:hover:not(:disabled) {
					background: #f8f9fa;
					color: #5c50e6;
				}
				.sa-conv-product-slider-btn:disabled {
					opacity: 0.35;
					cursor: default;
				}
			`}</style>
			<div style={{ position: "relative", paddingLeft: 4, paddingRight: 4 }}>
				<button
					type="button"
					className="sa-conv-product-slider-btn"
					style={{ left: 0 }}
					disabled={!canPrev}
					aria-label="Previous products"
					onClick={() => scrollByDir(-1)}
				>
					<i className="fa fa-chevron-left" style={{ fontSize: 12 }} aria-hidden="true" />
				</button>
				<button
					type="button"
					className="sa-conv-product-slider-btn"
					style={{ right: 0 }}
					disabled={!canNext}
					aria-label="Next products"
					onClick={() => scrollByDir(1)}
				>
					<i className="fa fa-chevron-right" style={{ fontSize: 12 }} aria-hidden="true" />
				</button>
				<div
					ref={trackRef}
					className="sa-conv-product-slider-track"
					style={{
						display: "flex",
						gap: SLIDER_GAP,
						paddingBottom: 6,
						paddingLeft: 36,
						paddingRight: 36,
					}}
				>
					{products.map((p, i) => {
						const href = resolveUrlAgainstWebsite(p.url, websiteBaseUrl);
						const img = resolveUrlAgainstWebsite(p.image, websiteBaseUrl);
						return (
							<a
								key={i}
								href={href || "#"}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(e) => {
									if (!href) e.preventDefault();
								}}
								style={{
									flex: "0 0 auto",
									width: SLIDER_CARD_W,
									textDecoration: "none",
									color: "inherit",
									border: "1px solid #dee2e6",
									borderRadius: 8,
									padding: 8,
									background: "#fff",
								}}
							>
								<div
									style={{
										width: "100%",
										height: 72,
										borderRadius: 4,
										background: "#f1f3f5",
										marginBottom: 6,
										overflow: "hidden",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									{img ? (
										<img src={img} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
									) : (
										<i className="fa fa-image text-muted" aria-hidden="true" />
									)}
								</div>
								<div
									style={{
										fontSize: 12,
										fontWeight: 500,
										lineHeight: 1.25,
										display: "-webkit-box",
										WebkitLineClamp: 2,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
									}}
								>
									{p.name || "Product"}
								</div>
								{p.sku ? (
									<div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
										{p.sku}
									</div>
								) : null}
								{p.price != null && p.price !== "" ? (
									<div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: "#333" }}>
										${Number(p.price).toFixed(2)}
									</div>
								) : null}
							</a>
						);
					})}
				</div>
			</div>
		</div>
	);
}
